import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribeToUsers,
  updateUserRoles,
  subscribeToAppRoles,
  addAppRole,
  deleteUser,
  subscribeToDbUsage,
  calculateDbUsage,
} from '../utils/storage';
import { ROLE_INFO, formatRoleLabel } from '../utils/permissions';
import logoWhite from '../assets/logo-white.png';
import logoDark from '../assets/logo-dark.png';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import './RolesPage.css';
import './Modal.css';

const KNOWN_ROLES = ['admin', 'reporter', 'operator', 'account_manager', 'assessor', 'tirp_organizer'];

export default function RolesPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logo = theme === 'dark' ? logoWhite : logoDark;

  const [users,          setUsers]          = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [editingUser,    setEditingUser]    = useState(null);
  const [editRoles,      setEditRoles]      = useState([]);
  const [showManageRoles, setShowManageRoles] = useState(false);
  const [showRolesInfo,   setShowRolesInfo]   = useState(false);
  const [newRoleName,    setNewRoleName]    = useState('');
  const [saving,         setSaving]         = useState(false);
  const [dbUsage,        setDbUsage]        = useState({ usedGb: 0, limitGb: 1 });

  useEffect(() => {
    const unsub = subscribeToUsers(setUsers);
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = subscribeToAppRoles(setAvailableRoles);
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = subscribeToDbUsage(setDbUsage);
    return () => unsub();
  }, []);

  // Auto-calculate real DB usage from document counts on page load
  useEffect(() => { calculateDbUsage(); }, []);

  function handleUserClick(user) {
    setEditingUser(user);
    setEditRoles([...(user.roles || [])]);
  }

  function toggleRole(role) {
    setEditRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  }

  async function handleSaveRoles() {
    if (!editingUser) return;
    setSaving(true);
    try {
      await updateUserRoles(editingUser.uid, editRoles);
      // Optimistic update — reflect the saved roles immediately so the user
      // sees the change without waiting for the next subscribeToUsers tick.
      setUsers((prev) =>
        prev.map((u) =>
          (u.uid === editingUser.uid || u.id === editingUser.id)
            ? { ...u, roles: [...editRoles] }
            : u,
        ),
      );
      setEditingUser(null);
    } catch (err) {
      console.error('Failed to update roles:', err);
    }
    setSaving(false);
  }

  async function handleDeleteUser() {
    if (!editingUser) return;
    const step1 = window.confirm(
      `Are you sure you want to delete the user "${editingUser.displayName || editingUser.email}"?`
    );
    if (!step1) return;
    const step2 = window.confirm(
      `This action cannot be undone. The user will be permanently removed from the system. Confirm?`
    );
    if (!step2) return;
    setSaving(true);
    try {
      await deleteUser(editingUser.uid);
      // Optimistic removal — drop the row immediately.
      setUsers((prev) => prev.filter((u) =>
        !(u.uid === editingUser.uid || u.id === editingUser.id)
      ));
      setEditingUser(null);
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
    setSaving(false);
  }

  async function handleAddRole() {
    const trimmed = newRoleName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!trimmed || availableRoles.includes(trimmed)) return;
    setSaving(true);
    try {
      await addAppRole(trimmed);
      // Optimistic insert — show the new role in the list immediately.
      setAvailableRoles((prev) => prev.includes(trimmed) ? prev : [...prev, trimmed]);
      setNewRoleName('');
    } catch (err) {
      console.error('Failed to add role:', err);
    }
    setSaving(false);
  }

  // ── DB usage bar ─────────────────────────────────────────────
  // Values stored as GB in Firestore; display converted to KB (1 GB = 1,000,000 KB)
  const usedGb   = dbUsage.usedGb  || 0;
  const limitGb  = dbUsage.limitGb || 1;
  const usedKb   = usedGb  * 1_000_000;
  const limitKb  = limitGb * 1_000_000;
  const usagePct = Math.min(100, (usedGb / limitGb) * 100);
  const barColor = usagePct >= 90 ? 'danger' : usagePct >= 70 ? 'warning' : 'ok';

  return (
    <div className="roles-page">

      {/* ── Header ── */}
      <div className="roles-header">
        <div className="roles-header-left">
          <img src={logo} alt="Terra Inspectioneering" className="roles-header-logo" />
          <div className="roles-header-title-block">
            <span className="roles-header-line1">Terra Inspectioneering</span>
            <span className="roles-header-line2">Admin Management</span>
          </div>
        </div>

        {/* DB usage bar — center */}
        <div className="roles-header-center">
          <div className="roles-db-label">Database Usage</div>
          <div className="roles-db-bar-track">
            <div
              className={`roles-db-bar-fill roles-db-bar-${barColor}`}
              style={{ width: `${usagePct}%` }}
            />
          </div>
          <div className="roles-db-text">
            {Math.round(usedKb).toLocaleString()} KB / {Math.round(limitKb).toLocaleString()} KB
          </div>
        </div>

        <div className="roles-header-right">
          <ThemeToggle />
          <button className="roles-back-btn" onClick={() => navigate('/')}>
            &larr; Back to Portal
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="roles-content">
        <div className="roles-toolbar">
          <h2 className="roles-subtitle">All Users ({users.length})</h2>
          <div className="roles-toolbar-btns">
            <button className="btn btn-secondary btn-sm" onClick={() => setShowRolesInfo(true)}>
              Roles Info
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowManageRoles(true)}>
              Manage Roles
            </button>
          </div>
        </div>

        {/* Users table */}
        <div className="roles-table">
          <div className="roles-table-header">
            <span className="roles-col-user-hd">User</span>
            <span className="roles-col-info-hd">Info</span>
          </div>

          {users.map((u) => (
            <div
              key={u.id}
              className="roles-table-row"
              onClick={() => handleUserClick(u)}
              title="Click to edit roles"
            >
              {/* Avatar + Name */}
              <div className="roles-col-user">
                {u.photoURL ? (
                  <img className="roles-avatar" src={u.photoURL} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <div className="roles-avatar-placeholder">
                    {(u.displayName || u.email || '?')[0].toUpperCase()}
                  </div>
                )}
                <span className="roles-user-name">{u.displayName || 'No Name'}</span>
              </div>

              {/* Info: email + role chips stacked */}
              <div className="roles-col-info">
                <span className="roles-user-email">{u.email}</span>
                <div className="roles-chips-row">
                  {(u.roles || []).length === 0 ? (
                    <span className="roles-chip pending">Pending</span>
                  ) : (
                    u.roles.map((r) => (
                      <span key={r} className="roles-chip">{formatRoleLabel(r)}</span>
                    ))
                  )}
                </div>
              </div>
            </div>
          ))}

          {users.length === 0 && (
            <div className="roles-table-empty">No users found.</div>
          )}
        </div>
      </div>

      {/* ── Edit User Roles Modal ── */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Roles</h2>
              <button className="modal-close" onClick={() => setEditingUser(null)}>&#10005;</button>
            </div>
            <div className="roles-edit-user-info">
              <p className="roles-edit-name">{editingUser.displayName || 'No Name'}</p>
              <p className="roles-edit-email">{editingUser.email}</p>
            </div>
            <div className="roles-toggle-list">
              {availableRoles.map((role) => (
                <label key={role} className="roles-toggle-item">
                  <input
                    type="checkbox"
                    checked={editRoles.includes(role)}
                    onChange={() => toggleRole(role)}
                  />
                  <span className="roles-toggle-label">{formatRoleLabel(role)}</span>
                </label>
              ))}
            </div>
            <div className="modal-actions modal-actions-spread">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleDeleteUser}
                disabled={saving}
                title="Permanently delete this user"
              >
                Delete User
              </button>
              <div className="modal-actions-right">
                <button className="btn btn-secondary" onClick={() => setEditingUser(null)} disabled={saving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveRoles} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Roles'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Roles Modal ── */}
      {showManageRoles && (
        <div className="modal-overlay" onClick={() => setShowManageRoles(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Role Names</h2>
              <button className="modal-close" onClick={() => setShowManageRoles(false)}>&#10005;</button>
            </div>

            {/* Existing roles list */}
            <div className="roles-manage-list">
              {availableRoles.map((role) => (
                <div key={role} className="roles-manage-item">
                  <span className="roles-manage-name">{formatRoleLabel(role)}</span>
                  <span className="roles-manage-key">{role}</span>
                </div>
              ))}
            </div>

            {/* Add new role */}
            <div className="roles-manage-add">
              <p className="roles-manage-add-label">Add New Role</p>
              <div className="roles-manage-add-row">
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. inspector"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddRole()}
                  className="roles-manage-input"
                />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleAddRole}
                  disabled={saving || !newRoleName.trim()}
                >
                  {saving ? '...' : 'Add'}
                </button>
              </div>
              <p className="roles-add-hint">Stored as lowercase with underscores (e.g., "account_manager").</p>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowManageRoles(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Roles Info Modal ── */}
      {showRolesInfo && (
        <div className="modal-overlay" onClick={() => setShowRolesInfo(false)}>
          <div className="modal modal-wide roles-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Role Permissions</h2>
              <button className="modal-close" onClick={() => setShowRolesInfo(false)}>&#10005;</button>
            </div>
            <div className="roles-info-grid">
              {KNOWN_ROLES.map((role) => {
                const info = ROLE_INFO[role];
                if (!info) return null;
                return (
                  <div key={role} className="roles-info-card">
                    <div className="roles-info-card-title">{formatRoleLabel(role)}</div>
                    {info.can.length > 0 && (
                      <ul className="roles-info-list">
                        {info.can.map((item, i) => (
                          <li key={i} className="roles-info-bullet roles-info-can">✓ {item}</li>
                        ))}
                      </ul>
                    )}
                    {info.cannot.length > 0 && (
                      <ul className="roles-info-list">
                        {info.cannot.map((item, i) => (
                          <li key={i} className="roles-info-bullet roles-info-cannot">✕ {item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowRolesInfo(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
